import { cn } from "@/lib/utils";

/**
 * Every band used to share one 28px heading, which left a 2,600px curriculum
 * looking exactly as important as a 265px tool list. Three tiers give the page
 * landmarks: `lg` for the two sections that carry the decision, `sm` for
 * supporting detail, `md` for everything else.
 */
export type ShowcaseHeaderSize = "sm" | "md" | "lg";

type Props = {
  eyebrow?: string;
  title: string;
  description?: string;
  className?: string;
  /** Optional id for the H2 (e.g. aria-labelledby targets). */
  titleId?: string;
  size?: ShowcaseHeaderSize;
};

const sizeClass: Record<ShowcaseHeaderSize, { wrap: string; eyebrow: string; title: string }> = {
  sm: {
    wrap: "mb-4",
    eyebrow: "mb-2 text-[11px] tracking-[0.2em]",
    title: "text-[19px] leading-snug md:text-[22px]",
  },
  md: {
    wrap: "mb-6",
    eyebrow: "mb-3 text-xs tracking-[0.22em]",
    title: "text-[24px] leading-[1.3] md:text-[28px] md:leading-snug",
  },
  lg: {
    wrap: "mb-8",
    eyebrow: "mb-3 text-xs tracking-[0.22em]",
    title: "text-[30px] leading-[1.2] md:text-[38px] md:leading-[1.15]",
  },
};

/** Eyebrow + H2 + optional subtitle — aligned with homepage SectionHeading rhythm; course neutrals. */
export function CourseShowcaseSectionHeader({
  eyebrow,
  title,
  description,
  className,
  titleId,
  size = "md",
}: Props) {
  const s = sizeClass[size];
  return (
    <div className={cn("max-w-2xl", s.wrap, className)}>
      {eyebrow ? (
        <p className={cn("font-semibold uppercase text-neutral-500", s.eyebrow)}>
          {eyebrow}
        </p>
      ) : null}
      <h2
        id={titleId}
        className={cn("font-semibold text-neutral-900", s.title)}
      >
        {title}
      </h2>
      {description ? (
        <p className="mt-2 max-w-2xl text-[15px] leading-[1.7] text-neutral-600">{description}</p>
      ) : null}
    </div>
  );
}
